import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Play, Pause, ArrowUpRight } from "@phosphor-icons/react";

const BASE = API.replace(/\/api$/, "");
const PREVIEW_SECONDS = 30;

export default function Spotlight() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => { api.get("/public/featured").then(({ data }) => setItems(data.releases)).catch(() => {}); }, []);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => {
      setProgress(Math.min(1, a.currentTime / PREVIEW_SECONDS));
      if (a.currentTime >= PREVIEW_SECONDS) { a.pause(); a.currentTime = 0; setActive(null); setProgress(0); }
    };
    const onEnd = () => { setActive(null); setProgress(0); };
    a.addEventListener("timeupdate", onTime); a.addEventListener("ended", onEnd);
    return () => { a.removeEventListener("timeupdate", onTime); a.removeEventListener("ended", onEnd); };
  }, []);

  const toggle = (r) => {
    const a = audioRef.current;
    if (active === r.id) { a.pause(); setActive(null); setProgress(0); return; }
    a.src = `${BASE}${r.preview_url}`;
    a.currentTime = 0;
    a.play().then(() => setActive(r.id)).catch(() => {});
  };

  if (items.length === 0) return null;

  return (
    <section id="roster" className="border-b border-zinc-800" data-testid="spotlight-section">
      <audio ref={audioRef} preload="none" />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="w-16 h-1.5 bg-red-600 mb-6" />
            <h2 className="font-display text-4xl lg:text-6xl uppercase tracking-tighter leading-[0.9]">On the<br />Roster<span className="text-red-600">.</span></h2>
          </div>
          <p className="hidden md:block text-zinc-500 text-sm max-w-xs text-right">Featured drops from Asylum artists. 30-second previews — press play.</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-zinc-800">
          {items.map((r) => {
            const playing = active === r.id;
            return (
              <div key={r.id} className="bg-black group relative border-r border-b border-zinc-800" data-testid={`spotlight-card-${r.id}`}>
                <div className="relative aspect-square overflow-hidden">
                  <img src={`${BASE}${r.cover_url}`} alt={`${r.title} by ${r.artist_name} cover art`} loading="lazy" className={`w-full h-full object-cover transition-all duration-500 ${playing ? "" : "grayscale group-hover:grayscale-0"}`} />
                  <button onClick={() => toggle(r)} data-testid={`spotlight-play-${r.id}`} aria-label={playing ? "Pause" : "Play preview"}
                    className={`absolute inset-0 flex items-center justify-center transition-opacity ${playing ? "opacity-100 bg-black/40" : "opacity-0 group-hover:opacity-100 bg-black/50"}`}>
                    <span className="w-16 h-16 bg-red-600 flex items-center justify-center">
                      {playing ? <Pause size={24} weight="fill" /> : <Play size={24} weight="fill" />}
                    </span>
                  </button>
                  {playing && <div className="absolute bottom-0 left-0 h-1 bg-red-600 transition-[width]" style={{ width: `${progress * 100}%` }} />}
                  <span className="absolute top-3 left-3 text-[10px] tracking-[0.2em] uppercase bg-black/80 px-2 py-1">{r.release_type}</span>
                </div>
                <div className="p-5">
                  <div className="font-display text-xl uppercase tracking-tight truncate">{r.title}</div>
                  <div className="text-sm text-zinc-400 truncate">{r.artist_name}</div>
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-[10px] tracking-widest uppercase text-zinc-600">{r.genre} · {r.tracks.length} trk</span>
                    <Link to={`/presave/${r.presave_slug}`} data-testid={`spotlight-link-${r.id}`} className="text-[10px] tracking-[0.2em] uppercase text-red-500 hover:text-white inline-flex items-center gap-1">
                      {r.status === "live" ? "Listen" : "Pre-Save"} <ArrowUpRight size={10} />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
